/**
 * Product Banner Component
 *
 * 功能：
 * 1. 视频自动播放控制（含 iOS Safari）
 * 2. 移动端滚动时显示固定按钮
 * 3. 产品颜色变体联动
 */

const PRODUCT_BANNER_MOBILE_MQ = '(max-width: 749px)';

class ProductBanner {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.productHandle = section.dataset.productHandle;

    this.videoContainer = section.querySelector('.product-banner__video-container');
    this.desktopVideo = section.querySelector('.product-banner__video');
    this.mobileVideo = section.querySelector('.product-banner__video--mobile');
    this.video = null;
    this.inactiveVideo = null;

    this.stickyCta = section.querySelector('[data-sticky-cta]');
    this.productLinks = section.querySelectorAll('[data-product-link]');

    this.nextSectionsAni = section.dataset.nextSectionsAni === 'true';

    this.isVideoPlaying = false;
    this.stickyCtaVisible = false;
    this._interactionFallbackAttached = false;

    this.windowHeight = window.innerHeight;
    this.scrollY = 0;
    this.selectedColor = null;
    this.rafId = null;

    this._onResizeVideo = this.handleViewportVideoChange.bind(this);

    this.init();
  }

  init() {
    this.initVideo();
    this.setupProductLinks();
    this.setupScrollHandlers();
    this.setupResizeObserver();

    this.section.classList.add('is-loaded');
    this.section.classList.remove('is-loading');
  }

  isMobileViewport() {
    return window.matchMedia(PRODUCT_BANNER_MOBILE_MQ).matches;
  }

  /**
   * 选择当前视口应播放的视频，并暂停隐藏的那一个
   */
  resolveActiveVideo() {
    const mobile = this.mobileVideo;
    const desktop = this.desktopVideo;

    if (this.isMobileViewport() && mobile) {
      this.video = mobile;
      this.inactiveVideo = desktop;
    } else {
      this.video = desktop || mobile || this.videoContainer?.querySelector('video') || null;
      this.inactiveVideo = this.video === mobile ? desktop : mobile;
    }

    if (this.inactiveVideo) {
      try {
        this.inactiveVideo.pause();
        this.inactiveVideo.removeAttribute('autoplay');
      } catch (e) {}
    }

    return this.video;
  }

  handleViewportVideoChange() {
    const hadVideo = !!this.video;
    const wasPlaying = this.isVideoPlaying;

    this.resolveActiveVideo();

    if (!this.video) return;

    if (hadVideo && this.video) {
      this.configureVideoElement(this.video);
    }

    if (wasPlaying) {
      this.isVideoPlaying = false;
    }

    this.tryPlayVideo(true);
  }

  configureVideoElement(video) {
    if (!video) return;

    video.controls = false;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    if (!video.getAttribute('preload')) {
      video.setAttribute('preload', 'auto');
    }
  }

  initVideo() {
    if (!this.resolveActiveVideo()) {
      this.initIframeCover();
      return;
    }

    this.configureVideoElement(this.video);

    const onCanPlay = () => this.tryPlayVideo();
    this.video.addEventListener('loadeddata', onCanPlay, { once: true });
    this.video.addEventListener('canplay', onCanPlay);

    this.video.addEventListener('play', () => {
      this.onVideoPlaying();
    });

    this.video.addEventListener('ended', () => {
      this.isVideoPlaying = false;

      if (!this.video.loop && this.videoContainer) {
        try {
          const d = this.video.duration || 0;
          if (d > 0) this.video.currentTime = Math.max(0, d - 0.001);
        } catch (e) {}
        this.video.pause();
        this.video.removeAttribute('poster');
        this.videoContainer.classList.add('is-ended');
      }
    });

    if ('IntersectionObserver' in window) {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          if (entry.isIntersecting) {
            this.tryPlayVideo();
          } else if (this.isVideoPlaying) {
            this.pauseVideo();
          }
        },
        { threshold: 0.05 }
      );

      this.videoObserver.observe(this.section);
    }

    window.matchMedia(PRODUCT_BANNER_MOBILE_MQ).addEventListener('change', this._onResizeVideo);

    this.tryPlayVideo(true);

    setTimeout(() => {
      this.tryPlayVideo(true);
    }, 200);
  }

  /**
   * YouTube/Vimeo iframe 无 play 事件：封面短暂展示后隐藏，避免永久盖住视频
   */
  initIframeCover() {
    if (!this.videoContainer) return;

    const iframe = this.videoContainer.querySelector('.product-banner__video-iframe');
    const cover = this.videoContainer.querySelector('.product-banner__cover');
    if (!iframe || !cover) return;

    const reveal = () => {
      this.videoContainer.classList.add('is-playing');
    };

    // 给封面一点预加载展示时间，再让出给 iframe 自动播放
    this._iframeCoverTimer = setTimeout(reveal, 800);

    iframe.addEventListener(
      'load',
      () => {
        if (this._iframeCoverTimer) {
          clearTimeout(this._iframeCoverTimer);
          this._iframeCoverTimer = null;
        }
        setTimeout(reveal, 400);
      },
      { once: true }
    );
  }

  onVideoPlaying() {
    this.isVideoPlaying = true;

    if (this.video) {
      this.video.removeAttribute('poster');
      this.video.classList.add('is-playing');
      this.video.style.opacity = '1';
    }

    if (this.videoContainer) {
      this.videoContainer.classList.add('is-playing');
      this.videoContainer.classList.remove('autoplay-blocked');
    }
  }

  attachInteractionFallback() {
    if (this._interactionFallbackAttached) return;
    this._interactionFallbackAttached = true;

    if (this.videoContainer) {
      this.videoContainer.classList.add('autoplay-blocked');
    }

    const resumeOnInteraction = () => {
      this.tryPlayVideo(true);
      document.removeEventListener('click', resumeOnInteraction);
      document.removeEventListener('touchstart', resumeOnInteraction);
    };

    document.addEventListener('click', resumeOnInteraction, { once: true, passive: true });
    document.addEventListener('touchstart', resumeOnInteraction, { once: true, passive: true });
  }

  tryPlayVideo(force = false) {
    if (!this.video) return;

    if (this.isVideoPlaying && !force) return;

    this.video.muted = true;
    this.video.defaultMuted = true;

    const playPromise = this.video.play();

    if (!playPromise || typeof playPromise.then !== 'function') {
      return;
    }

    playPromise
      .then(() => {
        this.onVideoPlaying();
      })
      .catch((error) => {
        console.log('Video autoplay prevented:', error);
        this.isVideoPlaying = false;
        this.attachInteractionFallback();
      });
  }

  pauseVideo() {
    if (!this.video || !this.isVideoPlaying) return;

    this.video.pause();
    this.isVideoPlaying = false;
  }

  setupProductLinks() {
    if (!this.productLinks.length) return;

    if (this.selectedColor === null) {
      this.selectedColor = this.getSelectedColor();
    }

    if (this.selectedColor) {
      const origin = window.location.origin;
      this.productLinks.forEach((link) => {
        const url = new URL(link.href, origin);
        url.searchParams.set('variant_color', this.selectedColor);
        link.href = url.toString();
      });
    }
  }

  getSelectedColor() {
    if (!this._urlParams) {
      this._urlParams = new URLSearchParams(window.location.search);
    }
    const urlColor = this._urlParams.get('color');
    if (urlColor) return urlColor;

    return this.section.dataset.selectedColor || '';
  }

  setupScrollHandlers() {
    const handleScroll = () => {
      if (this.rafId !== null) return;

      this.rafId = window.requestAnimationFrame(() => {
        this.updateScrollState();
        this.rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    this._scrollHandler = handleScroll;

    this.updateScrollState();
  }

  updateScrollState() {
    this.scrollY = window.scrollY || window.pageYOffset;

    const scrollProgress = Math.min(this.scrollY / this.windowHeight, 1);
    const translateY = scrollProgress * -100;

    if (this.nextSectionsAni) {
      this.section.style.transform = `translateY(${translateY}%)`;
    } else {
      this.section.style.transform = '';
    }

    if (this.stickyCta) {
      const shouldShow = this.scrollY > this.windowHeight * 0.8;

      if (shouldShow !== this.stickyCtaVisible) {
        this.stickyCtaVisible = shouldShow;
        this.stickyCta.classList.toggle('is-visible', shouldShow);
      }
    }
  }

  setupResizeObserver() {
    this.resizeRafId = null;
    const handleResize = () => {
      if (this.resizeRafId !== null) return;

      this.resizeRafId = requestAnimationFrame(() => {
        this.windowHeight = window.innerHeight;
        this.updateScrollState();
        this.resizeRafId = null;
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });
    this._resizeHandler = handleResize;
  }

  destroy() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    if (this._iframeCoverTimer) {
      clearTimeout(this._iframeCoverTimer);
      this._iframeCoverTimer = null;
    }

    window.matchMedia(PRODUCT_BANNER_MOBILE_MQ).removeEventListener('change', this._onResizeVideo);

    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }

    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.resizeRafId !== null) {
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }

    this.pauseVideo();
  }
}

function initProductBanner() {
  const sections = document.querySelectorAll('.product-banner');
  const instances = [];

  sections.forEach((section) => {
    const instance = new ProductBanner(section);
    instances.push(instance);
  });

  window.productBannerInstances = instances;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductBanner);
} else {
  initProductBanner();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('.product-banner');
    if (section) {
      new ProductBanner(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('.product-banner');
    if (section && window.productBannerInstances) {
      const instance = window.productBannerInstances.find((inst) => inst.section === section);
      if (instance) {
        instance.destroy();
        const index = window.productBannerInstances.indexOf(instance);
        window.productBannerInstances.splice(index, 1);
      }
    }
  });
}
